import React, { useEffect, useRef, useState } from 'react';

import Loading from '@/components/common/Loading';

import styles from './index.module.scss';

export interface VideoPlayerProps {
  src: string;
  title?: string;
  onClose: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, title, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleWaiting = () => {
      // Delay showing loading to avoid flashing on quick seeks
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      loadingTimerRef.current = setTimeout(() => {
        setIsLoading(true);
        loadingTimerRef.current = null;
      }, 300); // Only show loading if buffering takes more than 300ms
    };

    const handlePlaying = () => {
      // Cancel delayed loading if video starts playing
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setIsLoading(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplaythrough', handleCanPlay);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplaythrough', handleCanPlay);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);

      // Clear loading timer on cleanup
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isDragging]);

  // Format time in seconds to MM:SS format
  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) {
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const progressBar = progressBarRef.current;
    if (!video || !progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    handleProgressClick(e);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const video = videoRef.current;
      const progressBar = progressBarRef.current;
      if (!video || !progressBar) return;

      const rect = progressBar.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;

      video.currentTime = newTime;
      setCurrentTime(newTime);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={styles.compareModalOverlay} onClick={onClose}>
      {/* 弹窗容器 */}
      <div className={styles.compareModalDialog} onClick={(e) => e.stopPropagation()}>
        {/* 标题栏 */}
        <div className={styles.compareModalHeader}>
          <span className={styles.compareModalTitle}>{title || 'Preview'}</span>
          {/* 关闭按钮 */}
          <div className={styles.compareModalCloseBtn} onClick={onClose}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path
                d="M17 5L5 17M5 5L17 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* 内容区域 */}
        <div className={styles.compareModalBody}>
          {/* 视频容器 */}
          <div className={styles.compareModalVideoWrapper}>
            <video
              ref={videoRef}
              src={src}
              className={styles.video}
              onClick={togglePlay}
            />
            {isLoading && (
              <div className={styles.videoLoading}>
                <Loading />
              </div>
            )}

            {/* 底部控制条 */}
            <div className={styles.controls}>
              {/* 播放/暂停按钮 */}
              <div className={styles.playButton} onClick={togglePlay}>
                {isPlaying ? (
                  <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                    <rect x="8" y="6" width="3" height="14" rx="1" fill="white" />
                    <rect x="15" y="6" width="3" height="14" rx="1" fill="white" />
                  </svg>
                ) : (
                  <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                    <path
                      d="M9 6L20 13L9 20V6Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1"
                    />
                  </svg>
                )}
              </div>

              {/* 进度条区域 */}
              <div className={styles.progressContainer}>
                <span className={styles.time}>{formatTime(currentTime)}</span>
                <div
                  ref={progressBarRef}
                  className={styles.progressBar}
                  onClick={handleProgressClick}
                  onMouseDown={handleProgressMouseDown}
                >
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                <span className={styles.time}>{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
